// client/src/pages/Profile.js
import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_API_URL}/api`;

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [postedTasks, setPostedTasks] = useState([]);
  const [appliedTasks, setAppliedTasks] = useState([]);
  const [historyTasks, setHistoryTasks] = useState([]);

  const [postedOpps, setPostedOpps] = useState([]); // NEW
  const [postedLostFound, setPostedLostFound] = useState([]); // NEW: lost & found posts by user
  const [menuOpen, setMenuOpen] = useState(null);

  const [cancelTask, setCancelTask] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const [view, setView] = useState("posted");

  useEffect(() => {
    if (!user) return;
    loadTasks();
    loadOpportunities(); // NEW
    loadLostFound(); // NEW - load user's lost & found posts
  }, [user]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (!e.target.closest(".task-menu-wrapper")) setMenuOpen(null);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* =========================================================
        LOAD TASKS
  ========================================================= */
  const loadTasks = async () => {
    try {
      const res = await fetch(`${API}/tasks?includeExpired=true`);
      const all = await res.json();
      // API returns an array of tasks
      if (!Array.isArray(all)) {
        // Some endpoints return { task } objects in other contexts; be defensive
        console.warn("Expected tasks array, got:", all);
        setPostedTasks([]);
        setAppliedTasks([]);
        setHistoryTasks([]);
        return;
      }

      const now = new Date();

      const posted = all.filter((t) => {
        const isOwner = String(t.postedBy) === String(user.id);
        const isExpired = t.dueDate && new Date(t.dueDate) < now;
        const isCompleted = t.status === "completed";
        return isOwner && !isExpired && !isCompleted;
      });

      const applied = all.filter((t) => {
        const isExpired = t.dueDate && new Date(t.dueDate) < now;
        if (isExpired || t.status === "completed") return false;

        const applied = Array.isArray(t.applicants) &&
          t.applicants.some((a) => String(a.userId) === String(user.id));

        const accepted =
          t.acceptedApplicant &&
          String(t.acceptedApplicant) === String(user.id);

        return applied || accepted;
      });

      const history = all.filter((t) => {
        const isOwnerHistory =
          String(t.postedBy) === String(user.id) &&
          (t.status === "completed" || t.status === "expired");

        const isApplicantHistory =
          t.status === "completed" &&
          t.acceptedApplicant &&
          String(t.acceptedApplicant) === String(user.id);

        return isOwnerHistory || isApplicantHistory;
      });

      setPostedTasks(posted);
      setAppliedTasks(applied);
      setHistoryTasks(history);
    } catch (e) {
      console.error("Task load error:", e);
    }
  };

  /* =========================================================
        LOAD OPPORTUNITIES (RECRUIT POSTS)
  ========================================================= */
  const loadOpportunities = async () => {
    try {
      const res = await fetch(`${API}/opportunities`);
      const d = await res.json();
      if (!res.ok) {
        console.warn("Failed to load opportunities", d);
        return;
      }

      const ops = d.items || [];

      const mine = ops.filter((o) => String(o.postedBy) === String(user.id));

      setPostedOpps(mine);
    } catch (err) {
      console.error("Opp load error:", err);
    }
  };

  /* =========================================================
        LOAD LOST & FOUND (NEW)
  ========================================================= */
  const loadLostFound = async () => {
    try {
      const res = await fetch(`${API}/lost-found`);
      const d = await res.json();
      if (!res.ok) {
        console.warn("Failed to load lost & found", d);
        return;
      }

      const items = d.items || [];
      const mine = items.filter((i) => String(i.postedBy) === String(user.id));
      setPostedLostFound(mine);
    } catch (err) {
      console.error("LostFound load error:", err);
    }
  };

  /* =========================================================
        DELETE TASK
  ========================================================= */
  const deleteTask = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;

    try {
      const token = localStorage.getItem("cp_token");

      const res = await fetch(`${API}/tasks/${taskId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const d = await res.json();
      if (!res.ok) return alert(d.error || "Delete failed");

      alert("Task deleted successfully");
      loadTasks();
    } catch (err) {
      console.error(err);
      alert("Error deleting task");
    }
  };

  /* =========================================================
        DELETE OPPORTUNITY (NEW)
  ========================================================= */
  const deleteOpp = async (oppId) => {
    if (!window.confirm("Delete this opportunity permanently?")) return;

    try {
      const token = localStorage.getItem("cp_token");

      const res = await fetch(`${API}/opportunities/${oppId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const d = await res.json();
      if (!res.ok) return alert(d.error || "Delete failed");

      alert("Opportunity deleted.");
      loadOpportunities();
    } catch (err) {
      console.error(err);
      alert("Error deleting opportunity");
    }
  };

  /* =========================================================
        DELETE LOST & FOUND POST (NEW)
  ========================================================= */
  const deleteLostFound = async (lostId) => {
    if (!window.confirm("Delete this lost/found post permanently?")) return;

    try {
      const token = localStorage.getItem("cp_token");

      const res = await fetch(`${API}/lost-found/${lostId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const d = await res.json();
      if (!res.ok) return alert(d.error || "Delete failed");

      alert("Lost & Found post deleted.");
      loadLostFound();
    } catch (err) {
      console.error(err);
      alert("Error deleting lost & found post");
    }
  };

  /* ========================================================= */

  const prettyStatus = (s) => {
    switch (s) {
      case "applied":
        return "Applied — Waiting";
      case "in_progress":
        return "Accepted";
      case "pending_completion":
        return "Pending Confirmation";
      case "completed":
        return "Completed";
      case "expired":
        return "Expired";
      default:
        return "Open";
    }
  };

  if (!user)
    return (
      <div className="min-h-screen flex justify-center items-center text-lg dark:text-white">
        Please login to view your profile.
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 px-6 py-12 text-slate-900 dark:text-slate-100">
      <div className="max-w-6xl mx-auto">

        {/* HEADER */}
        <h1 className="text-4xl font-extrabold text-center text-indigo-600 dark:text-indigo-400 mb-10">
          Your Profile
        </h1>

        {/* PROFILE CARD */}
        <div className="bg-white dark:bg-slate-800 shadow-xl rounded-2xl p-8 mb-8">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            <img
              src={
                user.profilePhoto ||
                "https://cdn-icons-png.flaticon.com/512/3135/3135715.png"
              }
              className="w-28 h-28 rounded-full shadow-md ring-4 ring-indigo-500/20"
              alt="profile"
            />

            <div className="w-full text-center sm:text-left break-words">
              <h2 className="text-2xl sm:text-3xl font-bold break-words">{user.name}</h2>
              <p className="text-slate-600 dark:text-slate-300 break-all">{user.email}</p>
              <p className="text-slate-600 dark:text-slate-300">College: {user.college}</p>
              <p className="text-slate-600 dark:text-slate-300">Year: {user.year}</p>
              <p className="mt-2 text-sm text-slate-700 dark:text-slate-200">
                Credit Score: <strong>{user.creditScore ?? 100}</strong>
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-4 w-full">
            <button
              onClick={() => navigate("/profile/edit")}
              className="bg-indigo-600 text-white px-5 py-2 rounded-lg hover:bg-indigo-700"
            >
              Edit Profile
            </button>

            <button
              onClick={logout}
              className="bg-red-600 text-white px-5 py-2 rounded-lg hover:bg-red-700"
            >
              Logout
            </button>
          </div>
        </div>

        {/* TABS */}
        <div className="flex gap-3 mb-6 flex-wrap justify-center sm:justify-start">
          {["posted", "applied", "history", "opportunities", "lostfound"].map((tab) => (
            <button
              key={tab}
              onClick={() => setView(tab)}
              className={`px-4 py-2 rounded transition ${
                view === tab
                  ? "bg-indigo-600 text-white"
                  : "bg-white dark:bg-slate-800 dark:text-slate-200"
              }`}
            >
              {tab === "lostfound" ? "Lost & Found" : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* POSTED TASKS */}
        {view === "posted" && (
          <div className="space-y-4 mb-10">
            {postedTasks.length === 0 && (
              <p className="text-slate-600 dark:text-slate-300">No active posted tasks.</p>
            )}

            {postedTasks.map((task, index) => (
              <div
                key={task._id}
                className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow flex justify-between"
              >
                <div>
                  <h3 className="font-bold">{task.title}</h3>
                  <p className="text-slate-700 dark:text-slate-300">{task.category}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Status: {prettyStatus(task.status)}
                  </p>
                </div>

                <div className="relative task-menu-wrapper">
                  <button
                    onClick={() =>
                      setMenuOpen(menuOpen === index ? null : index)
                    }
                    className="text-xl px-3"
                  >
                    ⋮
                  </button>

                  {menuOpen === index && (
                    <div className="absolute right-0 bg-white dark:bg-slate-700 shadow rounded w-44 border border-slate-200 dark:border-slate-600">
                      {/* Manage Applicants opens the TaskDetails page (option A) */}
                      <button
                        onClick={() => navigate(`/task/${task._id}`)}
                        className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-600"
                      >
                        Manage Applicants
                      </button>

                      {/* FIXED: use task._id (was task._1) */}
                      <button
                        onClick={() => navigate(`/task/${task._id}/edit`)}
                        className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-600"
                      >
                        Edit Task
                      </button>

                      <button
                        onClick={() => deleteTask(task._id)}
                        className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-100 dark:hover:bg-slate-600"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* APPLIED TASKS */}
       {view === "applied" && (
  <div className="space-y-4 mb-10">
    {appliedTasks.length === 0 && (
      <p className="text-slate-600 dark:text-slate-300">
        No active applied tasks.
      </p>
    )}

    {appliedTasks.map((task) => {
      const isAccepted =
        task.acceptedApplicant &&
        String(task.acceptedApplicant) === String(user.id);

      const someoneElseAccepted =
        task.acceptedApplicant &&
        String(task.acceptedApplicant) !== String(user.id);

      return (
        <div
          key={task._id}
          className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow"
        >
          <div
            onClick={() => navigate(`/task/${task._id}`)}
            className="cursor-pointer"
          >
            <h3 className="font-bold">{task.title}</h3>
            <p className="text-slate-700 dark:text-slate-300">
              {task.category}
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Status: {prettyStatus(task.status)}
            </p>

            {/* If USER is accepted */}
            {isAccepted && (
              <p className="text-green-600 dark:text-green-400 mt-1">
                Assigned — Continue chat with the owner.
              </p>
            )}

            {/* If SOMEONE ELSE is accepted */}
            {someoneElseAccepted && (
              <p className="mt-2 text-yellow-700 dark:text-yellow-400 text-sm">
                Another applicant has been selected — you may wait or cancel (no penalty).
              </p>
            )}
          </div>

          {/* Cancel button stays same */}
          <button
            onClick={() => setCancelTask(task)}
            className={`mt-3 px-4 py-1 rounded text-white ${
              isAccepted ? "bg-red-600" : "bg-indigo-600"
            }`}
          >
            {isAccepted ? "Cancel (Penalty)" : "Cancel Application"}
          </button>
        </div>
      );
    })}
  </div>
)}


        {/* HISTORY */}
        {view === "history" && (
          <div className="space-y-4 mb-10">
            {historyTasks.length === 0 && (
              <p className="text-slate-600 dark:text-slate-300">
                No completed/expired tasks yet.
              </p>
            )}

            {historyTasks.map((task) => (
              <div
                key={task._id}
                className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow"
              >
                <h3 className="font-bold">{task.title}</h3>
                <p className="text-slate-700 dark:text-slate-300">{task.category}</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Status: {prettyStatus(task.status)}
                </p>

                <p className="text-xs mt-1 text-slate-500 dark:text-slate-400">
                  Posted: {new Date(task.createdAt).toLocaleDateString()}
                </p>

                {task.status === "completed" && (
                  <p className="text-green-500 dark:text-green-400 text-sm mt-1">
                    Completed: {new Date(task.completedAt).toLocaleDateString()}
                  </p>
                )}

                {task.status === "expired" && (
                  <p className="text-red-500 dark:text-red-400 text-sm mt-1">
                    Expired: {task.dueDate?.slice(0, 10)}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* OPPORTUNITIES (NEW TAB) */}
        {view === "opportunities" && (
          <div className="space-y-4 mb-10">
            {postedOpps.length === 0 && (
              <p className="text-slate-600 dark:text-slate-300">
                You haven’t posted any opportunities yet.
              </p>
            )}

            {postedOpps.map((op) => (
              <div
                key={op._id}
                className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow flex justify-between"
              >
                <div>
                  <h3 className="font-bold">{op.title}</h3>
                  <p className="text-slate-700 dark:text-slate-300">
                    {op.category}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Posted: {new Date(op.createdAt).toLocaleString()}
                  </p>
                </div>

                <div className="flex flex-col items-end gap-2">
                  <button
                    onClick={() => navigate(`/recruit/${op._id}`)}
                    className="px-3 py-1 border rounded dark:border-gray-600 dark:text-white"
                  >
                    View
                  </button>

                  <button
                    onClick={() => deleteOpp(op._id)}
                    className="px-3 py-1 bg-red-600 text-white rounded"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* LOST & FOUND (NEW TAB) */}
        {view === "lostfound" && (
          <div className="space-y-4 mb-10">
            {postedLostFound.length === 0 && (
              <p className="text-slate-600 dark:text-slate-300">
                You haven’t posted any lost & found items yet.
              </p>
            )}

            {postedLostFound.map((item) => (
              <div
                key={item._id}
                className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow flex justify-between"
              >
                <div>
                  <h3 className="font-bold">{item.title}</h3>
                  <p className="text-slate-700 dark:text-slate-300">{item.location}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Posted: {new Date(item.createdAt).toLocaleString()}
                  </p>
                </div>

                <div className="flex flex-col items-end gap-2">
                  <button
                    onClick={() => navigate(`/lost-found/${item._id}`)}
                    className="px-3 py-1 border rounded dark:border-gray-600 dark:text-white"
                  >
                    View
                  </button>

                  <button
                    onClick={() => deleteLostFound(item._id)}
                    className="px-3 py-1 bg-red-600 text-white rounded"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CANCEL POPUP */}
        {cancelTask && (
          <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
            <div className="bg-white dark:bg-slate-800 p-8 rounded-xl w-80 shadow-xl border dark:border-slate-600">
              <h2 className="text-xl font-bold text-red-600 dark:text-red-400">
                Cancel Application?
              </h2>

              <p className="mt-3 text-slate-700 dark:text-slate-300">
                {String(cancelTask.acceptedApplicant) === String(user.id)
                  ? "Cancelling may reduce your credit score."
                  : "No penalty will be applied."}
              </p>

              <div className="flex justify-between mt-6">
                <button
                  onClick={() => setCancelTask(null)}
                  className="px-4 py-2 bg-gray-300 dark:bg-slate-700 text-black dark:text-white rounded"
                >
                  Back
                </button>

                <button
                  onClick={async () => {
                    setCancelLoading(true);
                    try {
                      const token = localStorage.getItem("cp_token");
                      const res = await fetch(
                        `${API}/tasks/${cancelTask._id}/cancel`,
                        {
                          method: "POST",
                          headers: { Authorization: `Bearer ${token}` },
                        }
                      );
                      const d = await res.json();
                      setCancelLoading(false);
                      if (!res.ok) return alert(d.error || "Cancel failed");
                      alert("Application cancelled.");
                      loadTasks();
                      setCancelTask(null);
                    } catch (err) {
                      setCancelLoading(false);
                      alert("Error");
                    }
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded"
                >
                  {cancelLoading ? "..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
